import { useCallback, useEffect, useRef, useState } from 'react'
import type { Paginated } from '@/api/types'

const STRING_FILTER_KEYS = new Set(['q', 'name', 'slug', 'search'])
const DEFAULT_LIMIT = 20
const DEFAULT_DEBOUNCE_MS = 300

type Fetcher<T, F> = (params: { limit: number; offset: number } & F) => Promise<Paginated<T>>

interface Options<F> {
  initialLimit?: number
  initialFilters?: F
  debounceMs?: number
  enabled?: boolean
}

interface PaginatedListResult<T, F> {
  items: T[]
  total: number
  loading: boolean
  error: string | null
  page: number
  totalPages: number
  limit: number
  filters: F
  setFilter: <K extends keyof F>(key: K, value: F[K]) => void
  setPage: (page: number) => void
  reload: () => void
  patchItems: (updater: (items: T[]) => T[]) => void
}

export function usePaginatedList<T, F extends Record<string, unknown>>(
  fetcher: Fetcher<T, F>,
  options: Options<F> = {},
): PaginatedListResult<T, F> {
  const {
    initialLimit = DEFAULT_LIMIT,
    initialFilters = {} as F,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    enabled = true,
  } = options

  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPageState] = useState(1)
  const [filters, setFilters] = useState<F>(initialFilters)
  const [appliedFilters, setAppliedFilters] = useState<F>(initialFilters)
  const [limit] = useState(initialLimit)
  // State-based counter so useEffect re-runs on reload()
  const [reloadTick, setReloadTick] = useState(0)

  const abortRef = useRef<AbortController | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const offset = (page - 1) * limit
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const executeFetch = useCallback(
    (currentFilters: F, currentOffset: number, currentLimit: number) => {
      if (!enabled) return

      // Cancel any pending deferred call and in-flight request
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setLoading(true)
      setError(null)

      // Defer one tick so React StrictMode's synchronous cleanup can cancel
      // the first invocation before it ever reaches the network.
      timerRef.current = setTimeout(() => {
        timerRef.current = null
        if (controller.signal.aborted) return
        fetcher({ limit: currentLimit, offset: currentOffset, ...currentFilters })
          .then((data) => {
            if (controller.signal.aborted) return
            setItems(data.results)
            setTotal(data.count)
            setLoading(false)
          })
          .catch((err: unknown) => {
            if (controller.signal.aborted) return
            setError(err instanceof Error ? err.message : 'Erro ao carregar dados')
            setLoading(false)
          })
      }, 0)
    },
    [fetcher, enabled],
  )

  useEffect(() => {
    if (!enabled) return
    executeFetch(appliedFilters, offset, limit)
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      abortRef.current?.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, offset, limit, enabled, reloadTick])

  const setFilter = useCallback(
    <K extends keyof F>(key: K, value: F[K]) => {
      const newFilters = { ...filters, [key]: value } as F
      setFilters(newFilters)

      const isStringKey = typeof key === 'string' && STRING_FILTER_KEYS.has(key as string)

      if (debounceRef.current) clearTimeout(debounceRef.current)

      if (isStringKey) {
        debounceRef.current = setTimeout(() => {
          setAppliedFilters(newFilters)
          setPageState(1)
        }, debounceMs)
      } else {
        setAppliedFilters(newFilters)
        setPageState(1)
      }
    },
    [debounceMs, filters],
  )

  const setPage = useCallback((newPage: number) => {
    setPageState(newPage)
  }, [])

  const reload = useCallback(() => {
    setAppliedFilters(filters)
    setPageState(1)
    setReloadTick((t) => t + 1)
  }, [filters])

  const patchItems = useCallback((updater: (items: T[]) => T[]) => {
    setItems((current) => updater(current))
  }, [])

  return {
    items,
    total,
    loading,
    error,
    page,
    totalPages,
    limit,
    filters,
    setFilter,
    setPage,
    reload,
    patchItems,
  }
}
